import api from '@/lib/axios';

export interface UserRpgProfile {
  id: string;
  userId: string;
  level: number;
  currentXp: string;
  points: string;
  class?: string;
  avatarUrl?: string;
  dailyXpHistory?: {
    [date: string]: {
      whatsapp: number;
      manual: number;
      notion: number;
      personal: number;
      calendar: number;
      playbook: number;
      platform_usage?: number;
      tech_system?: number;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface LevelProgress {
  level: number;
  currentXp: number;
  xpForLevel: number;
  xpNeededForNextLevel: number;
  progress: number;
}

export interface XpLevelEntry {
  level: number;
  xpForLevel: number;
  cumulativeXp: number;
  totalXpToReachNext: number;
}

export interface XpLevelTable {
  levels: XpLevelEntry[];
  maxLevel: number;
}

export interface UpdateProfileDto {
  class?: string;
}

export interface AddXpResponse {
  success: boolean;
  levelUp: boolean;
  newLevel?: number;
  xpAdded: number;
}

export interface DailyObjective {
  type: string;
  label: string;
  current: number;
  limit: number;
  progress: number;
  points: number;
}

export interface DailyObjectivesResponse {
  objectives: DailyObjective[];
}

export const gamificationService = {
  getProfile: async (): Promise<UserRpgProfile> => {
    const { data } = await api.get<UserRpgProfile>('/gamification/profile');
    return data;
  },

  getLevelProgress: async (): Promise<LevelProgress> => {
    const { data } = await api.get<LevelProgress>('/gamification/profile/progress');
    return data;
  },

  updateProfile: async (dto: UpdateProfileDto): Promise<UserRpgProfile> => {
    const { data } = await api.patch<UserRpgProfile>('/gamification/profile', dto);
    return data;
  },

  uploadAvatar: async (file: File): Promise<{ avatarUrl: string }> => {
    const formData = new FormData();
    formData.append('avatar', file);
    const { data } = await api.post<{ avatarUrl: string }>(
      '/gamification/profile/avatar',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return data;
  },

  addXp: async (taskType: 'whatsapp' | 'capture' | 'manual' | 'notion'): Promise<AddXpResponse> => {
    const { data } = await api.post<AddXpResponse>('/gamification/add-xp', { taskType });
    return data;
  },

  getDailyObjectives: async (): Promise<DailyObjectivesResponse> => {
    const { data } = await api.get<DailyObjectivesResponse>('/gamification/daily-objectives');
    return data;
  },

  getXpLevelTable: async (): Promise<XpLevelTable> => {
    const { data } = await api.get<XpLevelTable>('/gamification/xp-levels');
    return data;
  },

  getXpRules: async (): Promise<
    Array<{
      id?: string;
      taskType: string;
      label: string;
      points: number;
      dailyLimit: number;
      active: boolean;
      sortOrder: number;
    }>
  > => {
    const { data } = await api.get('/gamification/admin/xp-rules');
    return data;
  },

  updateXpRules: async (
    rules: Array<{
      taskType: string;
      label?: string;
      points?: number;
      dailyLimit?: number;
      active?: boolean;
      sortOrder?: number;
    }>,
  ) => {
    const { data } = await api.patch('/gamification/admin/xp-rules', { rules });
    return data;
  },

  getTeamXpOverrides: async (): Promise<TeamXpOverridesResponse> => {
    const { data } = await api.get('/gamification/admin/team-xp-overrides');
    return data;
  },

  updateTeamXpOverrides: async (
    overrides: TeamXpOverridePayload[],
  ): Promise<TeamXpOverridesResponse> => {
    const { data } = await api.put('/gamification/admin/team-xp-overrides', {
      overrides,
    });
    return data;
  },
};

export interface TeamXpOverrideRow {
  id?: string;
  teamId: string;
  teamName?: string;
  taskType: string;
  points: number;
  active: boolean;
}

export interface TeamXpOverridePayload {
  teamId: string;
  taskType: string;
  points: number;
  active?: boolean;
}

export interface TeamXpOverridesResponse {
  teams: Array<{ id: string; name: string }>;
  overrides: TeamXpOverrideRow[];
}
